import { Injectable } from '@angular/core';
import { AmorphicService } from '../amorphic/amorphic.service';
import {
  Conjunction,
  GrammarTable,
  Noun,
  PointOfView,
  Predicate,
  PredicateType,
  ProperNoun,
  Tense,
  NounPhrase,
  Verb,
} from './GrammarTable';
import { PartyType } from '../../../js/interfaces/CoverpathTypes';
import { UICategory, UILoggerSingleton } from '../../../../common/js/logger/UILoggerSingleton';

/**
 * A service for taking predicate expressions, and converting them into text!
 *
 * Expressions are JSON objects double wrapped like angular bindings
 * The `GrammarTable.Predicate` type defines these properties.
 *
 * Please see `predicate.service.test.ts` and current App Questions for usage examples.
 *
 * Also does WoP and ECP text replacements
 */
@Injectable()
export class PredicateService {
  private readonly predicateExp: RegExp = /\{\{.*?\}\}/g;
  controller;

  constructor(private amorphicService: AmorphicService) {
    this.controller = this.amorphicService.controller;
  }

  /**
   * Look through the question text and process any predicate blocks
   *
   * @param qText The full text of the question
   * @param pronounLocked Whether or not the text of the question is 'locked', or not to be modified (except for WoP)
   * @param loopIndex
   *
   * @remarks Predicate blocks are of the form `{{"noun": "owner", "verb" : "to do", "type": "normal"}}`
   * which translate to a `Predicate` object
   */
  public parseForPredicates(qText: string, pronounLocked: boolean, loopIndex: number): string {
    // First check if WOP needs to be inserted
    let parsedText = this.insertWOPDetails(qText);

    parsedText = this.insertECPDetails(parsedText);

    if (pronounLocked) {
      return parsedText;
    }

    const matches: string[] = parsedText.match(this.predicateExp);

    if (matches) {
      matches.map((match) => {
        try {
          let preparse = match;
          preparse = preparse.slice(1, -1); // trim extra brackets before parsing json
          preparse = preparse.replace(/('|`)/g, `"`); // replace single or tick with double quotes
          // and use a reviver to parse verbs
          const predicate: Predicate = JSON.parse(preparse, (key, value) => {
            if (key === 'verb') {
              return this.getVerb(value as string);
            }
            return value;
          });
          const replacementText: string = this.generatePredicate(predicate);
          parsedText = parsedText.replace(match, replacementText);
        } catch (err) {
          const error = {
            isHumanRelated: false,
            name: err.name,
            message: err.message,
            stack: err.stack,
            data: {
              text: match,
            },
          };
          UILoggerSingleton.getInstance().error({
            module: 'PredicateService',
            function: 'parseForPredicates',
            category: UICategory.request,
            error: error,
          });
        }
      });
    }

    return parsedText;
  }

  /**
   * Inserts WOP details if needed, using old bindster tags
   *
   * @param input Question Text
   */
  insertWOPDetails(input: string): string {
    const application = this.controller.customer.application;

    const primaryWOPRegex = /<b:premiumwaiver><\/b:premiumwaiver>/;
    const secondaryWOPRegex = /<b:premiumwaiversecondary><\/b:premiumwaiversecondary>/;

    const isPrimary = primaryWOPRegex.test(input);
    const isSecondary = secondaryWOPRegex.test(input);

    if (!isPrimary && !isSecondary) {
      return input;
    }

    const policy = isPrimary ? application.primaryPolicy : application.secondaryPolicy;

    if (
      policy &&
      (policy.isCoverpathPolicy() || policy.isMMDirectPolicy()) &&
      ((isPrimary && !policy.isWopEligibleForPrimaryPolicy()) ||
        (isSecondary && !policy.isWopEligibleForSecondaryPolicy()))
    ) {
      // question is not visible, so return question text as is
      return input;
    }

    const waiverOfPremiumAmount = policy && policy.premiumBreakdown && policy.premiumBreakdown.waiverOfPremiumAmount;
    const replacementRegex = isPrimary ? primaryWOPRegex : secondaryWOPRegex;

    const canInsertWOP =
      !!policy &&
      !!policy.paymentFrequency &&
      !!waiverOfPremiumAmount &&
      Number.isFinite(waiverOfPremiumAmount.valueOf());

    if (!canInsertWOP) {
      let errorMessage = '';
      errorMessage += !policy ? 'policy is undefined ' : '';
      errorMessage += !policy.paymentFrequency ? 'payment frequency is undefined ' : '';
      errorMessage += !waiverOfPremiumAmount ? 'WoP amount is undefined ' : '';
      errorMessage +=
        waiverOfPremiumAmount && !Number.isFinite(waiverOfPremiumAmount.valueOf()) ? 'WoP amount is not fininte ' : '';

      const error = {
        isHumanRelated: false,
        message: errorMessage,
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'PredicateService',
          function: 'insertWOPDetails',
          category: UICategory.request,
          error: error,
        },
        'Cannot insert waiver of premium details from policy. Missing some information.'
      );

      return input;
    }

    const annualRegEx = /annual/i;
    const wopFrequency = annualRegEx.test(policy.paymentFrequency) ? 'year' : 'month';

    return input
      .replace(replacementRegex, `$${waiverOfPremiumAmount.toFixed(2)}`)
      .replace(/<b:paymentfrequency><\/b:paymentfrequency>/, wopFrequency);
  }

  /**
   * Inserts ECP details if needed, using old bindster tags
   *
   * @param input Question Text
   */
  insertECPDetails(input: string): string {
    const application = this.controller.customer.application;

    const primaryECPRegex = /<b:ecpyears><\/b:ecpyears>/;
    const secondaryECPRegex = /<b:ecpyearssecondary><\/b:ecpyearssecondary>/;

    const isPrimary = primaryECPRegex.test(input);
    const isSecondary = secondaryECPRegex.test(input);

    if (!isPrimary && !isSecondary) {
      return input;
    }

    const policy = isPrimary ? application.primaryPolicy : application.secondaryPolicy;

    if (
      policy &&
      (policy.isCoverpathPolicy() || policy.isMMDirectPolicy()) &&
      ((isPrimary && !policy.isEcpEligibleForPrimaryPolicy()) ||
        (isSecondary && !policy.isEcpEligibleForSecondaryPolicy()))
    ) {
      // question is not visible, so return question text as is
      return input;
    }

    const replacementRegex = isPrimary ? primaryECPRegex : secondaryECPRegex;
    const baseWithECP = policy.premiumBreakdown.baseWithECP;
    const baseWithoutECP = policy.premiumBreakdown.baseWithoutECP;

    const canInsertECP =
      !!policy &&
      !!policy.paymentFrequency &&
      !!policy.productType &&
      !!policy.term &&
      !!baseWithECP &&
      !!baseWithoutECP &&
      !!policy.convertibleMaxYears;

    if (!canInsertECP) {
      let errorMessage = '';
      errorMessage += !policy ? 'policy is undefined ' : '';
      errorMessage += !policy.paymentFrequency ? 'payment frequency is undefined ' : '';
      errorMessage += !policy.productType ? 'product type is undefined ' : '';
      errorMessage += !policy.term ? 'term is undefined ' : '';
      errorMessage += !baseWithECP ? 'premium with ECP is undefined ' : '';
      errorMessage += !baseWithoutECP ? 'premium without ECP is undefined ' : '';
      errorMessage += !policy.convertibleMaxYears ? 'convertible max years is undefined ' : '';

      const error = {
        isHumanRelated: false,
        message: errorMessage,
      };
      UILoggerSingleton.getInstance().error(
        {
          module: 'PredicateService',
          function: 'insertECPDetails',
          category: UICategory.request,
          error: error,
        },
        'Cannot insert Extended Convertibility Period (ECP) details from policy. Missing some information.'
      );

      return input;
    }

    const annualRegEx = /annual/i;
    const ecpFrequency = annualRegEx.test(policy.paymentFrequency) ? 'year' : 'month';

    const ecpYears = policy.convertibleMaxYears;
    if (ecpYears) {
      const ecpAdditionalCost = baseWithECP - baseWithoutECP;

      input = input
        .replace(replacementRegex, `${ecpYears}`)
        .replace(/<b:ecpadditionalcost><\/b:ecpadditionalcost>/, `$${ecpAdditionalCost.toFixed(2)}`)
        .replace(/<b:paymentfrequency><\/b:paymentfrequency>/, ecpFrequency);
    }

    return input;
  }

  /**
   * Construct the predicate--or phrase--that's represented by the `Predicate` object
   * @param predicate represents the phrase in value form
   * @returns a string representing the predicate in phrase form
   */
  generatePredicate(predicate: Predicate): string {
    const noun: Noun = this.getYouOrPartyName(predicate.noun, predicate.useFullName, predicate.conjunction);
    const POV: PointOfView = this.getPointOfView(noun);
    const verb: Verb = predicate.verb;
    const tense: Tense = predicate.tense ? predicate.tense : Tense.present;
    const additionalVerb: string = predicate.secondTense ? '/' + verb[predicate.secondTense][POV] : '';
    let nounPhrase: string = this.parseNounPhrase(predicate.nounPhrase, noun.isPlural);
    nounPhrase = nounPhrase ? ` ${nounPhrase}` : '';

    let result = '';

    switch (predicate.type) {
      case PredicateType.normal:
        result = `${noun.Subject} ${verb[tense][POV]}${additionalVerb}${nounPhrase}`;
        break;
      case PredicateType.inverted:
        result = `${verb[tense][POV]}${additionalVerb} ${noun.Subject}`;
        break;
      case PredicateType.object:
        result = noun.Object;
        break;
      case PredicateType.subject:
        result = noun.Subject;
        break;
      case PredicateType.possessive:
        result = noun.Possessive;
        break;
      default:
        UILoggerSingleton.getInstance().error(
          {
            module: 'PredicateService',
            function: 'generatePredicate',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          `Unsupported predicate type ${predicate.type}, unable to process`
        );
        break;
    }

    if (predicate.capitalize) {
      result = result.charAt(0).toUpperCase() + result.slice(1);
    }

    return result;
  }

  /**
   * If the applicant is also the given party, return the `You` pronoun object,
   * otherwise return a new proper noun with that person's given name
   * @param party the person's role
   * @param useFullName if passed in as true then return the full name of the party, otherwise return the first name
   * @returns the pronoun `You` or a proper noun with the given name
   */
  getYouOrPartyName(party: PartyType, useFullName: boolean, conjunction?: Conjunction): Noun {
    const policy = this.controller.customer.application.primaryPolicy;
    let partyRole = '';
    let partyName = '';
    let secondPartyName = '';

    switch (party) {
      case PartyType.owner:
        partyRole = policy.ownerClassification || policy.selfOwner;
        if (partyRole === 'trust' && policy.trustOrOtherEntity) {
          partyName =
            policy.trustOrOtherEntity.type === 'trust'
              ? policy.trustOrOtherEntity.trustName
              : policy.trustOrOtherEntity.name;
        } else if (policy.isJointOwnership()) {
          if (conjunction === Conjunction.any) {
            return new ProperNoun('any owner');
          } else if (conjunction === Conjunction.we) {
            const ret = GrammarTable.Pronouns.We;
            ret.isPlural = true;
            return ret;
          } else if (conjunction === Conjunction.both) {
            partyName = policy.isOwnerSameAsInsured()
              ? policy.insured.person.getFullName()
              : policy.ownerPerson.getFullName();
            secondPartyName = policy.jointOwnerIsInsured()
              ? policy.insured.person.getFullName()
              : policy.otherOwner.getFullName();
            return new ProperNoun(partyName, secondPartyName, Conjunction.and);
          } else if (conjunction) {
            partyName = 'you';
            secondPartyName = policy.jointOwnerIsInsured()
              ? policy.insured.person.getFullName()
              : policy.otherOwner.getFullName();
            return new ProperNoun(partyName, secondPartyName, conjunction);
          } else if (policy.isOwnerSameAsInsured()) {
            // you and someone else
            return GrammarTable.Pronouns.You;
          } else if (policy.ownerPerson) {
            partyName = useFullName ? policy.ownerPerson.getFullName() : policy.ownerPerson.firstName;
          }
        } else if (partyRole !== 'you' && policy.ownerPerson) {
          partyName = useFullName ? policy.ownerPerson.getFullName() : policy.ownerPerson.firstName;
        }
        break;
      case PartyType.insured:
        partyRole = policy.insuredType;
        partyName = useFullName ? policy.insured.person.getFullName() : policy.insured.person.firstName;
        break;
      case PartyType.signer:
        partyName = useFullName ? policy.insured.signer.getFullName() : policy.insured.signer.firstName;
        break;
      case PartyType.otherOwner:
        if (policy.jointOwnerIsInsured()) {
          partyName = useFullName ? policy.insured.person.getFullName() : policy.insured.person.firstName;
        } else {
          partyName = useFullName ? policy.otherOwner.getFullName() : policy.otherOwner.firstName;
        }
        break;
      case PartyType.contingentOwner:
        partyName = useFullName ? policy.contingentOwnerPerson.getFullName() : policy.contingentOwnerPerson.firstName;
        break;
      case PartyType.payer:
        if (policy.payer.getIsEntityOrTrust()) {
          partyName = policy.payer.name;
        } else {
          partyName = useFullName ? policy.payer.getFullName() : policy.payer.firstName;
        }
        break;
      default:
        UILoggerSingleton.getInstance().error(
          {
            module: 'PredicateService',
            function: 'getYouOrPartyName',
            category: UICategory.request,
            error: {
              isHumanRelated: false,
            },
          },
          `Unsupported predicate type ${party}, unable to process`
        );
        return;
    }

    if (partyRole === 'you') {
      return GrammarTable.Pronouns.You;
    }

    // If there has been no name specified in the application yet, use the appropriate party placeholder
    partyName = !!partyName ? partyName : `The ${this.toTitleCase(party.toString())}`;

    return new ProperNoun(partyName, secondPartyName, conjunction);
  }

  /**
   * Retrieves the proper `PointOfView` object from the given noun
   * @param noun a pronoun or proper noun
   * @returns third for a proper noun, then corresponding POVs for pronouns
   */
  getPointOfView(noun: Noun): PointOfView {
    if (noun instanceof ProperNoun) {
      return noun.isPlural ? PointOfView.ThirdPlural : PointOfView.Third;
    }

    switch (noun) {
      case GrammarTable.Pronouns.You:
        return PointOfView.Second;
      case GrammarTable.Pronouns.I:
        return PointOfView.First;
      case GrammarTable.Pronouns.We:
        return PointOfView.FirstPlural;
      case GrammarTable.Pronouns.They:
        return PointOfView.ThirdPlural;
      default:
        return PointOfView.Third;
    }
  }

  /**
   * Retrieves the corresponding `Verb` object from the given string
   * @param verb value from the initial `Predicate` object
   * @returns a `Verb` or undefined if none is available
   *
   * @remarks Accepts a broad variety of formats, being case and spacing agnostic,
   * the `to` of the infinitive is also optional. Example formats:
   * `To Have`,
   * `to have`,
   * `have`,
   * `tohave`
   */
  getVerb(verb: string): Verb {
    if (!verb) {
      return undefined;
    }

    verb = verb.trim().toLowerCase();

    if (!verb.startsWith('to')) {
      verb = `to ${verb}`;
    } else {
      verb = verb.slice(0, 2) + ' ' + verb.slice(2);
    }

    verb = this.toTitleCase(verb);
    return GrammarTable.Verbs[verb];
  }

  /**
   * Transforms a given string into Title Case
   * @param str The given string to convert
   * @remarks strips any and all spaces
   */
  toTitleCase(str: string): string {
    if (!str) {
      return str;
    }

    str = str.trim().toLowerCase();

    const matches = str.match(/\S+/g);
    if (!matches) {
      return '';
    }

    return matches
      .map((match) => {
        return match.charAt(0).toUpperCase() + match.slice(1);
      })
      .join('');
  }

  parseNounPhrase(nounPhrase: NounPhrase, isPlural: boolean): string {
    if (!nounPhrase) {
      return '';
    }
    try {
      let headNoun = GrammarTable.Nouns[this.toTitleCase(nounPhrase.headNoun)];
      headNoun = isPlural ? headNoun.Plural : headNoun.Singular;

      return `${!isPlural ? 'a ' : ''}${!!nounPhrase.preModifier ? nounPhrase.preModifier + ' ' : ''}${headNoun}`;
    } catch {
      UILoggerSingleton.getInstance().error(
        {
          module: 'PredicateService',
          function: 'parseNounPhrase',
          category: UICategory.request,
          error: {
            isHumanRelated: false,
          },
        },
        `Unsupported predicate type ${nounPhrase}, unable to process`
      );
      return '';
    }
  }
}
